import { useEffect, useRef, useState } from 'react'
import { ChatDocumentsToolNames, DocsHubToolNames } from '@atbash-labs/ontology-tools'

import type {
  AgentOrchestratorPlanStep,
  AgentOrchestratorStepResult,
  LiveOrchestratorToolCall
} from '@axilon-apps/domains-ontology-ui-state'

/**
 * Every subagent's finalize-tool name plus the orchestrator's own
 * `finalize_plan_summary`. When one of these is in flight, the chat
 * status flips to "Finalizing…" so the user sees what the model is
 * doing in the last 1-3 seconds of a turn (instead of the stale verb of
 * the previous data tool, or a generic "Thinking…").
 */
const FINALIZE_TOOL_NAMES: ReadonlySet<string> = new Set([
  'finalize_answer',
  'finalize_plan_summary',
  'finalize_custom_agent_config_action',
  'finalize_ingest'
])

const READ_USER_GUIDE_TOOL_NAME = 'read_user_guide'
const CHAT_ARTIFACT_DISPATCH_TOOL_NAMES: ReadonlySet<string> = new Set([
  'create_chat_artifact',
  'revise_chat_artifact'
])
const DOCUMENT_WRITE_TOOL_NAMES = new Set([
  'write_document',
  ChatDocumentsToolNames.AddChatDocument,
  ChatDocumentsToolNames.EditChatDocument,
  DocsHubToolNames.AddOrEditDoc,
  DocsHubToolNames.FindAndReplaceDoc,
  DocsHubToolNames.FillTemplate
])

export type ChatStatus =
  | { kind: 'planning' }
  | { kind: 'building-artifact' }
  | { kind: 'writing-document' }
  | { kind: 'reading-user-guide' }
  | { kind: 'replying' }
  | { kind: 'thinking' }
  | { kind: 'finalizing' }
  | { kind: 'awaiting-user' }
  | { kind: 'idle' }

export interface ChatStatusInput {
  /** `null` while the orchestrator is still drafting its plan. */
  plan: AgentOrchestratorPlanStep[] | null
  /** Per-step results keyed by stepIndex. */
  stepResults: Record<number, AgentOrchestratorStepResult>
  /** All in-flight tool calls across the active subagent. */
  toolCalls: LiveOrchestratorToolCall[]
  /** Per-step pending text — non-empty means the subagent's LLM is currently writing prose. */
  pendingProgressByStep: Record<number, string>
  /** True when one or more cypher-write proposals are awaiting user accept/reject. */
  hasInFlightCypherWrites: boolean
  /** Orchestrator's synthesized final summary, if it has arrived. */
  finalSummary: string | null
}

/**
 * Derive the chat-pane status line from in-flight orchestrator state.
 * Priority order matters: when multiple states could apply, the
 * earlier-matching arm wins.
 *
 *   1. `awaiting-user` — cypher-write proposals need the user's call.
 *   2. `building-artifact` / `writing-document` — a blocking visualization or
 *      documents hand-off is in flight.
 *   3. `finalizing` — a `finalize_*` tool is in flight, OR every plan
 *      step has completed (orchestrator is in / about to enter its
 *      final-synthesis turn).
 *   4. `reading-user-guide` — the planner called `read_user_guide` (optional product/meta lookup).
 *   5. `replying` — text-only plan, no summary yet.
 *   6. `thinking` — any other in-flight state. The UI rotates through a
 *      verb pool so the line doesn't flash through distinct phrases for
 *      fast tool calls.
 *   7. `planning` — plan hasn't been emitted.
 *   8. `idle` — never returned for live turns. Past turns short-circuit
 *      the status line in `ChatTurn.extractPast` instead.
 *
 * NOTE: we intentionally do NOT idle on `finalSummary` content. The
 * `liveFinalSummary` field gets a character appended on every
 * `agent_orchestrator_final_summary_delta` event, so checking for
 * non-empty content would flip the status to `idle` ~300ms into the
 * orchestrator's synthesis turn — making `Finalizing…` invisible. The
 * live turn keeps showing `Finalizing…` for the full synthesis stream
 * until `tab.run` is cleared and past-turn rendering takes over.
 */
export function deriveChatStatus(input: ChatStatusInput): ChatStatus {
  if (input.hasInFlightCypherWrites) {
    return { kind: 'awaiting-user' }
  }

  const inFlight = input.toolCalls.filter((tc) => tc.status === 'running')

  const inFlightArtifactBuild = inFlight.find((tc) =>
    CHAT_ARTIFACT_DISPATCH_TOOL_NAMES.has(tc.name)
  )
  if (inFlightArtifactBuild !== undefined) {
    return { kind: 'building-artifact' }
  }

  if (inFlight.some((tc) => DOCUMENT_WRITE_TOOL_NAMES.has(tc.name))) {
    return { kind: 'writing-document' }
  }

  const inFlightFinalize = inFlight.find((tc) => FINALIZE_TOOL_NAMES.has(tc.name))
  if (inFlightFinalize !== undefined) {
    return { kind: 'finalizing' }
  }

  const inFlightReadGuide = inFlight.find((tc) => tc.name === READ_USER_GUIDE_TOOL_NAME)
  if (inFlightReadGuide !== undefined) {
    return { kind: 'reading-user-guide' }
  }

  const plan = input.plan
  if (plan === null) {
    return { kind: 'planning' }
  }

  const nextPendingStep = plan.find((step) => !(input.stepResults[step.index]?.completed ?? false))

  // Plan is fully complete — orchestrator is either between subagent
  // returns and its synthesis turn OR mid-stream writing the synthesis.
  // Either way, the right label is "Finalizing…" until the run ends.
  if (nextPendingStep === undefined) {
    return { kind: 'finalizing' }
  }

  const isTextOnlyPlan = plan.length === 1 && plan[0].intended_agent === 'text'
  if (isTextOnlyPlan) {
    return { kind: 'replying' }
  }

  // Any other in-flight state: a non-finalize tool is running, OR the
  // subagent's LLM is mid-stream, OR the orchestrator is between
  // dispatches. All read as "thinking" to the user — the UI rotates
  // through a verb pool (`useRotatingThinkingVerb`) so the line doesn't
  // flash through distinct phrases for fast tool calls.
  return { kind: 'thinking' }
}

/**
 * Loading verbs the chat sidebar rotates through while the status is
 * `thinking`. Tilted toward industrial / manufacturing diagnostic
 * vocabulary so the chat reads like an operations assistant rather than
 * a generic chatbot. Gerund form so they slot into `${verb}…` cleanly.
 */
export const THINKING_VERBS = [
  'Thinking',
  'Analyzing',
  'Calibrating',
  'Inspecting',
  'Diagnosing',
  'Surveying',
  'Tracing',
  'Profiling',
  'Probing',
  'Crunching'
] as const

const THINKING_VERB_ROTATE_MS = 30_000

/**
 * Render a `ChatStatus` value as the line shown under the user's prompt.
 * For `thinking`, the caller supplies the rotating verb (see
 * {@link useRotatingThinkingVerb}); the default fallback is `Thinking…`.
 */
export function statusLineFor(status: ChatStatus, thinkingVerb?: string): string | null {
  switch (status.kind) {
    case 'planning':
      return 'Planning…'
    case 'building-artifact':
      return 'Building visualization…'
    case 'writing-document':
      return 'Writing document…'
    case 'reading-user-guide':
      return 'Reading the user guide…'
    case 'replying':
      return 'Replying…'
    case 'thinking':
      return `${thinkingVerb ?? 'Thinking'}…`
    case 'finalizing':
      return 'Finalizing…'
    case 'awaiting-user':
      return 'Awaiting your decision…'
    case 'idle':
      return null
  }
}

/**
 * Stable rotating-verb hook for the `thinking` status. Picks a verb at
 * random and cycles every {@link THINKING_VERB_ROTATE_MS} while `active`
 * is true, avoiding consecutive repeats. Without rotation, very short
 * tool calls would flash the status line through a sequence of
 * tool-specific verbs (one per call) — rotating through a generic verb
 * pool keeps the line stable while still feeling alive.
 */
export function useRotatingThinkingVerb(active: boolean): string {
  const [verb, setVerb] = useState<string>(() => pickThinkingVerb(null))
  const previousVerbRef = useRef<string>(verb)

  useEffect(() => {
    if (!active) return
    const fresh = pickThinkingVerb(previousVerbRef.current)
    previousVerbRef.current = fresh
    setVerb(fresh)

    const id = window.setInterval(() => {
      const next = pickThinkingVerb(previousVerbRef.current)
      previousVerbRef.current = next
      setVerb(next)
    }, THINKING_VERB_ROTATE_MS)
    return () => {
      window.clearInterval(id)
    }
  }, [active])

  return verb
}

function pickThinkingVerb(previous: string | null): string {
  if (THINKING_VERBS.length <= 1) return THINKING_VERBS[0]
  while (true) {
    const candidate = THINKING_VERBS[Math.floor(Math.random() * THINKING_VERBS.length)]
    if (candidate !== previous) return candidate
  }
}
